import { useNavigate } from 'react-router-dom';
import { Package, AlertTriangle, Bell } from 'lucide-react';

/**
 * useNotificationAction Hook
 * Maps notification types to navigation targets
 * 
 * @returns {Object} - { getActionForNotification, actionMap }
 * 
 * Usage:
 * const { getActionForNotification } = useNotificationAction();
 * const action = getActionForNotification(notification.type);
 * // action = { page: '/admin/pre-release-tracker', label: 'View Pre-Release', icon: Package }
 */
export function useNotificationAction() {
    const navigate = useNavigate();

    // Map notification types to their target pages and actions
    const actionMap = {
        'pre_release_request': {
            page: '/admin/pre-release-tracker',
            label: 'View Pre-Release Request',
            icon: Package,
            color: 'text-blue-600'
        },
        'low_stock': {
            page: '/admin/inventory',
            label: 'Check Inventory',
            icon: AlertTriangle,
            color: 'text-orange-600'
        },
        'announcement': {
            page: null, // No navigation needed
            label: 'Announcement',
            icon: Bell,
            color: 'text-gray-600'
        }
    };

    /**
     * Get action details for a notification type
     * @param {string} type - Notification type
     * @returns {Object|null} - Action object or null if no action defined
     */
    const getActionForNotification = (type) => {
        return actionMap[type] || null;
    };

    /**
     * Navigate to the appropriate page for a notification
     * Passes the notification ID via URL params so target page can auto-select
     * 
     * @param {Object} notification - The notification object
     */
    const handleNotificationClick = (notification) => {
        const action = getActionForNotification(notification.type);

        if (!action || !action.page) {
            // No navigation action for this type
            return false;
        }

        // Build query params based on notification type
        const queryParams = new URLSearchParams();

        switch (notification.type) {
            case 'pre_release_request':
                // For pre-release notifications, don't pass filter - just pass focus ID
                // PreReleaseTracker will auto-select and open modal
                queryParams.set('focus', notification.preReleaseId || '');
                break;

            case 'low_stock': {
                // For low stock, filter to show that product and branch
                queryParams.set('productId', notification.productId || '');
                const productName = notification.productName || notification.product_name || notification.product?.name || '';
                if (productName) queryParams.set('productName', productName);
                if (notification.branchId !== undefined && notification.branchId !== null) {
                    queryParams.set('branchId', String(notification.branchId));
                }
                // Force re-apply even if clicking the same notification
                queryParams.set('ts', String(Date.now()));
                break;
            }

            default:
                break;
        }

        // Navigate with query params
        const url = queryParams.toString()
            ? `${action.page}?${queryParams.toString()}`
            : action.page;

        navigate(url);
        return true;
    };

    return {
        getActionForNotification,
        handleNotificationClick,
        actionMap
    };
}

export default useNotificationAction;
